import { useApi } from '@/composables/use-api.ts';
import { FlexUser } from '@/services/api/user-api.ts';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { CompanyDto } from '@/services/api/company-api.ts';

const AUTH_TOKEN = 'token';

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;

interface State {
  /** user/me API로 받아오는 내 정보 */
  flexUser: FlexUser;
  token?: string;
  companies: CompanyDto[] | undefined;
}

export const useAuthStore = defineStore('auth', {
  state: (): State => {
    return {
      flexUser: {} as FlexUser,
      token: localStorage.getItem(AUTH_TOKEN) ?? undefined,
      companies: undefined,
    };
  },

  getters: {
    isLoggedIn: (state) =>
      state.token && Boolean(state.flexUser.googleUser?.id),

    /** 로그인한 유저 정보 */
    user: (state) => ({
      name: state.flexUser.googleUser?.name ?? '',

      email: state.flexUser.googleUser?.email ?? '',

      /** 구글 사진 */
      photo: state.flexUser.googleUser?.photo ?? '',

      /** 팀명만 */
      team: state.flexUser.employee?.organization.name ?? '',

      /** 상위조직 + 팀명 */
      department: state.flexUser.employee?.organization.fullName ?? '',

      /** 회사 코드 */
      companyCd: state.flexUser.company?.cd ?? '',

      /** 직원 번호? */
      employeeNo: state.flexUser.employee?.no ?? 0,
    }),

    isOrigin: (state) => state.flexUser.employee?.originYn == 'Y',

    isManager: (state) => state.flexUser.manager,

    isUser: (state) => state.flexUser.roles.includes('USER'),
  },

  actions: {
    async loginAsync() {
      const { auth } = useApi();
      this.flexUser = await auth.getMyInfo();
    },

    setToken(token: string) {
      this.token = token;
      localStorage.setItem(AUTH_TOKEN, token);
    },

    async listCompanyByManagedMeAsync() {
      if (this.companies) return this.companies;

      const { company } = useApi();
      return (this.companies = await company.company.listByManagedMeAsync());
    },

    async changeCompanyAsync(companyCd: string) {
      const { company } = useApi();
      const token = await company.company.changeCompanyAsync(companyCd);
      this.setToken(token);
    },

    /** 백엔드 서버에서 로그인 후 리다이렉트 */
    goLogin() {
      window.location.href = `${API_ENDPOINT}/oauth2/authorization/keycloak?redirect_uri=${window.location.origin}/login`;
    },

    async logout() {
      this.token = undefined;
      localStorage.clear();
      window.location.href = `${API_ENDPOINT}/logout?redirect_uri=${window.location.origin}/login?logout`;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
